import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { startCase } from "lodash";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { useHistory, useLocation } from "react-router";

import {
  Banner,
  Button,
  Icon,
  Input,
  Text,
  Tooltip,
  toastError,
} from "@alpacahq/ui/old";

import { useGetAcatsTransfers, useGetOwnerDetails } from "~/v2/api/hooks";
import { FAQ } from "~/v2/components/common/FAQ";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { onTaxIdKeyDown } from "../../../entityOnboarding/util";
import { Layout } from "../../common/Layout";
import type { AcatsLocationState } from "../../common/types";
import { getContraBrokerDisplayName } from "../../common/util";
import { BackButton } from "../BackButton";
import { ORIGINATING_BROKER_FAQ } from "../originatingBroker/FAQ";
import {
  CannotProcessTransferModal,
  type CannotProcessTransferModalProps,
} from "./CannotProcessTransferModal";
import { type AccountInfoSchema, zAccountInfoSchema } from "./schema";

const AccountInfoPage = () => {
  const history = useHistory<AcatsLocationState>();
  const { state: locationState } = useLocation<AcatsLocationState>();

  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { details } = useGetOwnerDetails();

  const [
    isCannotProcessTransferModalOpen,
    setIsCannotProcessTransferModalOpen,
  ] = useState(false);

  const [cannotProcessTransfer, setCannotProcessTransfer] =
    useState<CannotProcessTransferModalProps["reason"]>(null);

  const { data: allTransfers } = useGetAcatsTransfers({
    accountId: currentAccount?.id ?? "",
    options: {
      enabled: !!currentAccount?.id,
    },
  });

  const pendingTransfers = useMemo(
    () =>
      allTransfers?.transfers.filter(
        (t) => t.status.transfer_status === "PENDING",
      ) ?? [],
    [allTransfers],
  );

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AccountInfoSchema>({
    resolver: zodResolver(zAccountInfoSchema),
    mode: "all",
    defaultValues: {
      account_number: locationState?.accountNumber ?? "",
      tax_id: locationState?.taxId ?? "",
    },
  });

  const accountNumber = watch("account_number");
  const taxId = watch("tax_id");
  const isSubmitDisabled = !accountNumber || !taxId;

  const onBackClicked = () =>
    history.replace({
      pathname: Path.ROUTE_PORTFOLIO_TRANSFER,
      state: undefined,
    });

  const onPreviousClicked = () =>
    history.replace({
      pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ORIGINATING_BROKER,
      state: locationState,
    });

  const onCloseCannotProcessTransferModal = () =>
    setIsCannotProcessTransferModalOpen(false);

  const onSubmitValid: SubmitHandler<AccountInfoSchema> = (data) => {
    if (!cannotProcessTransfer) {
      // push to asset holding page
      history.replace({
        pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ASSETS_HOLDING,
        state: {
          brokerName: locationState?.brokerName,
          brokerNumber: locationState?.brokerNumber,
          // remove spacing user could have included, already validated not empty
          accountNumber: data.account_number.trim(),
          taxId: data.tax_id.trim(),
        },
      });
    }
    if (cannotProcessTransfer) {
      setIsCannotProcessTransferModalOpen(true);
    }
  };

  const onSubmitInvalid: SubmitErrorHandler<AccountInfoSchema> = (_data) => {
    toastError("Please enter account number and tax ID.");
  };

  useEffect(() => {
    if (!locationState?.brokerName || !locationState?.brokerNumber) {
      history.replace({
        pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ORIGINATING_BROKER,
        state: locationState,
      });
    }
  }, [locationState, history.replace]);

  // check if user has pending transfer from same broker name and account number
  useEffect(() => {
    const ongoingTransfer = pendingTransfers.find(
      (transfer) =>
        transfer.contra_broker_number === locationState?.brokerNumber &&
        transfer.account_number === accountNumber,
    );

    if (ongoingTransfer) {
      setCannotProcessTransfer({
        type: "ongoing_transfer",
        data: ongoingTransfer,
      });
    }
  }, [pendingTransfers, locationState?.brokerNumber, accountNumber]);

  const brokerName = getContraBrokerDisplayName(
    locationState?.brokerName ?? "Broker",
  );

  return (
    <div>
      <BackButton
        onClick={onBackClicked}
        label="Back to Transfer Funds"
        className="mb-4"
      />
      <Layout
        title={`${brokerName} Account Information`}
        faq={<FAQ faq={ORIGINATING_BROKER_FAQ} />}
        className="mt-2 pt-0"
      >
        <div className="flex w-full max-w-md flex-col gap-4 pb-4">
          <div className="flex flex-col gap-1">
            <Text className="font-semibold text-gray-700 text-sm">
              Account type:
            </Text>
            <Text className="flex items-center gap-2 font-bold text-gray-700">
              {currentAccount?.account_type === "trading" ? (
                <>
                  Individual Account{" "}
                  <Tooltip content="We support the transfer of assets from Joint accounts at other brokerages to your individual account.">
                    <Icon name="InformationCircle" />
                  </Tooltip>
                </>
              ) : currentAccount?.account_type?.toLowerCase() === "ira" ? (
                <>
                  IRA - {startCase(currentAccount?.account_sub_type ?? "")}{" "}
                  Account
                </>
              ) : (
                startCase(currentAccount?.account_type ?? "")
              )}
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text className="font-semibold text-gray-700 text-sm">
              Full Name:
            </Text>
            <Text className="font-bold text-gray-700">
              {currentAccount?.name}
            </Text>
          </div>
        </div>
        <form
          className="flex w-full flex-col gap-4"
          onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}
        >
          <Input
            label="Account Number"
            placeholder="Enter your broker account number"
            {...register("account_number")}
            status={errors.account_number ? "error" : "neutral"}
            errorMsg={errors.account_number?.message}
          />
          <Input
            label="Tax ID"
            placeholder="Enter your Tax ID number"
            onKeyDown={onTaxIdKeyDown(details?.tax_id_type ?? "")}
            {...register("tax_id")}
            status={errors.tax_id ? "error" : "neutral"}
            errorMsg={errors.tax_id?.message}
          />
          <Banner
            variant="warning"
            message={
              <Text className="text-orange-700">
                Ensure that your <span className="font-bold">Full Name</span>,{" "}
                <span className="font-bold">Tax ID</span>, and{" "}
                <span className="font-bold">account type</span> match between
                your sending brokerage and Alpaca account.
              </Text>
            }
          />
          <div className="flex w-full items-center justify-between gap-2 pt-5">
            <Button
              variant="v2/stroke"
              type="button"
              onClick={onPreviousClicked}
            >
              Previous
            </Button>
            <Button
              variant="v2/filled"
              type="submit"
              disabled={isSubmitDisabled}
            >
              Continue
            </Button>
          </div>
        </form>
      </Layout>
      <CannotProcessTransferModal
        isOpen={isCannotProcessTransferModalOpen}
        reason={cannotProcessTransfer}
        onBack={onBackClicked}
        onConfirm={onCloseCannotProcessTransferModal}
      />
    </div>
  );
};

export default AccountInfoPage;
